import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
    Accordion, AccordionDetails, AccordionSummary, Box, Button, Card, Checkbox, Chip, CircularProgress,
    Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton, Table, TableBody,
    TableCell, TableHead, TableRow, TextField, Typography,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import DeleteIcon from '@material-ui/icons/Delete';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';

import {
    getSurvey, saveSurveyDraft, discardSurveyDraft, publishSurveyDraft, previewSurveyDraft,
    surveyPreviewUrl, fmtDateTime,
} from '../../services/surveys.service';

/**
 * Edit a survey's wording — draft, preview, publish.
 *
 * The form shows one input per text field the API lists in `text_schema`, and a checkbox
 * per flag it lists (today: whether each field of a details form is required). Question
 * structure, option codes, limits and rules are shown for context but cannot be changed.
 * Saving writes the survey's draft (the next version number); Preview opens the draft on
 * the public page for an hour; Publish makes it the live version.
 *
 * Material-UI v4, so styling is `style=` rather than `sx=`.
 */

// Fields shown even when the live text has none, so they can be added.
const ALWAYS_SHOWN = {
    root: ['title'],
    intro: ['kicker', 'title', 'start_label'],
    end: ['kicker', 'title', 'body'],
    question: ['kicker', 'title', 'helper'],
    option: ['label'],
    scale: ['label'],
    context: ['label'],
    other: ['label', 'placeholder'],
    extra: ['label', 'placeholder'],
    row: ['label', 'tagline'],
    statement: ['label'],
    stat: ['value', 'label'],
    field: ['label', 'placeholder'],
};

// Lists longer than this (e.g. 249 countries) start folded.
const FOLD_AFTER = 30;

const SECTION_TITLE = {
    context: 'Context box', other: '"Other" text box', extra: 'Optional text box',
    options: 'Options', rows: 'Areas', scale: 'Rating scale', statements: 'Statements', stats: 'Figures',
    fields: 'Fields',
};

const TYPE_LABEL = { single: 'Single choice', multi: 'Multiple choice', rank: 'Ranking', matrix: 'Areas × rating', form: 'Details form' };

const clone = (o) => JSON.parse(JSON.stringify(o));

const setAt = (obj, path, value) => {
    const next = clone(obj);
    let cur = next;
    path.slice(0, -1).forEach((k) => { cur = cur[k]; });
    cur[path[path.length - 1]] = value;
    return next;
};

const questionFacts = (q) => {
    const facts = [TYPE_LABEL[q.type] || q.type];
    if (q.type === 'rank' || q.type === 'multi') {
        if (q.min && q.min === q.max) facts.push(`exactly ${q.max}`);
        else if (q.max) facts.push(`up to ${q.max}`);
    }
    if (q.required) facts.push('required');
    if (q.show_if) facts.push('shown only when an earlier answer matches');
    return facts;
};

const SurveyEditor = () => {
    const { survey_code } = useParams();
    const history = useHistory();

    const [detail, setDetail] = useState(null);
    const [form, setForm] = useState(null);
    const [savedJson, setSavedJson] = useState('');
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState('');
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [confirm, setConfirm] = useState(null); // 'publish' | 'discard'
    const [unfolded, setUnfolded] = useState({}); // long lists opened, by path

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await getSurvey(survey_code);
            const d = res && res.data;
            if (!d) throw new Error('Could not load the survey.');
            const start = d.draft ? d.draft.definition : d.live.definition;
            setDetail(d);
            setForm(clone(start));
            setSavedJson(JSON.stringify(start));
        } catch (e) {
            setError((e && e.message) || 'Could not load the survey.');
        } finally {
            setLoading(false);
        }
    }, [survey_code]);

    useEffect(() => { load(); }, [load]);

    const dirty = form && JSON.stringify(form) !== savedJson;

    // Warn before leaving with unsaved edits.
    useEffect(() => {
        if (!dirty) return undefined;
        const onLeave = (e) => { e.preventDefault(); e.returnValue = ''; };
        window.addEventListener('beforeunload', onLeave);
        return () => window.removeEventListener('beforeunload', onLeave);
    }, [dirty]);

    const schema = detail ? detail.text_schema : {};
    const labels = detail ? detail.field_labels : {};
    const maxText = detail ? detail.max_text : 2000;
    const change = (path) => (e) => setForm((f) => setAt(f, path, e.target.value));

    const save = async () => {
        setBusy('save'); setError(''); setNotice('');
        try {
            const res = await saveSurveyDraft(survey_code, form);
            const draft = res && res.data;
            setDetail((d) => ({ ...d, draft: { ...draft } }));
            setForm(clone(draft.definition));
            setSavedJson(JSON.stringify(draft.definition));
            setNotice(`Draft v${draft.version} saved.`);
            return draft;
        } catch (e) {
            setError((e && e.message) || 'Could not save the draft.');
            return null;
        } finally {
            setBusy('');
        }
    };

    const preview = async () => {
        // Open the tab now: browsers block a window.open that follows an await.
        const tab = window.open('', '_blank');
        if (dirty && !(await save())) { if (tab) tab.close(); return; }
        setBusy('preview'); setError('');
        try {
            const res = await previewSurveyDraft(survey_code);
            const url = surveyPreviewUrl(survey_code, res.data.token);
            if (tab) tab.location.href = url; else window.open(url, '_blank');
        } catch (e) {
            if (tab) tab.close();
            setError((e && e.message) || 'Could not open a preview.');
        } finally {
            setBusy('');
        }
    };

    const publish = async () => {
        setBusy('publish'); setError('');
        try {
            const res = await publishSurveyDraft(survey_code);
            setConfirm(null);
            await load();
            setNotice(`Published v${res.data.version}. The public survey shows it within a minute.`);
        } catch (e) {
            setError((e && e.message) || 'Could not publish.');
            setConfirm(null);
        } finally {
            setBusy('');
        }
    };

    const discard = async () => {
        setBusy('discard'); setError('');
        try {
            await discardSurveyDraft(survey_code);
            setConfirm(null);
            await load();
            setNotice('Draft discarded.');
        } catch (e) {
            setError((e && e.message) || 'Could not discard the draft.');
            setConfirm(null);
        } finally {
            setBusy('');
        }
    };

    // ── Generic text form, driven by text_schema ─────────────────────────────

    // A render function, not a component: a component defined here would remount (and
    // lose focus) on every keystroke.
    const renderField = (type, node, keyName, path) => {
        const spec = schema[type] || {};
        const required = (spec.required || []).includes(keyName);
        const multiline = (spec.multiline || []).includes(keyName);
        return (
            <TextField
                key={keyName}
                fullWidth
                size="small"
                variant="outlined"
                label={`${labels[keyName] || keyName}${required ? ' *' : ''}`}
                value={node[keyName] || ''}
                onChange={change([...path, keyName])}
                multiline={multiline}
                minRows={multiline ? 1 : undefined}
                maxRows={multiline ? 8 : undefined}
                error={required && !(node[keyName] || '').trim()}
                inputProps={{ maxLength: maxText }}
                style={{ marginBottom: 12 }}
            />
        );
    };

    const textKeys = (type, node) => ((schema[type] && schema[type].text) || []).filter((k) =>
        k in node
        || (ALWAYS_SHOWN[type] || []).includes(k)
        || (type === 'question' && k === 'required_note' && node.required));

    const renderNode = (type, node, path) => {
        const spec = schema[type] || {};
        return (
            <>
                {textKeys(type, node).map((k) => renderField(type, node, k, path))}

                {(spec.flags || []).map((k) => (
                    <FormControlLabel
                        key={k}
                        style={{ display: 'block', marginTop: -4, marginBottom: 8 }}
                        control={(
                            <Checkbox
                                size="small"
                                color="primary"
                                checked={Boolean(node[k])}
                                onChange={(e) => setForm((f) => setAt(f, [...path, k], e.target.checked))}
                            />
                        )}
                        label={labels[k] || k}
                    />
                ))}

                {(spec.paragraphs || []).map((k) => (
                    <Box key={k} style={{ marginBottom: 12 }}>
                        <Typography variant="caption" style={{ color: '#666' }}>{labels[k] || k}</Typography>
                        {(node[k] || []).map((p, i) => (
                            <Box key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 6 }}>
                                <TextField fullWidth size="small" variant="outlined" multiline minRows={2} maxRows={8}
                                    value={p} onChange={change([...path, k, i])} inputProps={{ maxLength: maxText }} />
                                <IconButton size="small" aria-label="Remove paragraph"
                                    onClick={() => setForm((f) => setAt(f, [...path, k], node[k].filter((_, j) => j !== i)))}>
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        ))}
                        <Button size="small" style={{ marginTop: 6 }}
                            onClick={() => setForm((f) => setAt(f, [...path, k], [...(node[k] || []), '']))}>
                            + Add paragraph
                        </Button>
                    </Box>
                ))}

                {Object.entries(spec.objects || {}).filter(([k]) => node[k]).map(([k, child]) => (
                    <Box key={k} style={{ border: '1px solid #eee', borderRadius: 6, padding: 12, marginBottom: 12 }}>
                        <Typography variant="subtitle2" style={{ marginBottom: 8 }}>{SECTION_TITLE[k] || k}</Typography>
                        {renderNode(child, node[k], [...path, k])}
                    </Box>
                ))}

                {Object.entries(spec.lists || {}).filter(([k]) => Array.isArray(node[k]) && node[k].length).map(([k, child]) => {
                    const listKey = [...path, k].join('.');
                    const folded = node[k].length > FOLD_AFTER && !unfolded[listKey];
                    return (
                    <Box key={k} style={{ border: '1px solid #eee', borderRadius: 6, padding: 12, marginBottom: 12 }}>
                        <Typography variant="subtitle2" style={{ marginBottom: 8 }}>{SECTION_TITLE[k] || k}</Typography>
                        {folded && (
                            <Button size="small" onClick={() => setUnfolded((u) => ({ ...u, [listKey]: true }))}>
                                Show all {node[k].length} {(SECTION_TITLE[k] || k).toLowerCase()}
                            </Button>
                        )}
                        {!folded && node[k].map((item, i) => {
                            const id = schema[child] && schema[child].id ? item[schema[child].id] : String(i + 1);
                            return (
                                <Box key={id} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', borderTop: i ? '1px dashed #eee' : 'none', paddingTop: i ? 10 : 0 }}>
                                    <Typography variant="caption" style={{ width: 70, flex: 'none', paddingTop: 10, color: '#888', fontFamily: 'monospace' }}>{id}</Typography>
                                    <Box style={{ flex: 1 }}>{renderNode(child, item, [...path, k, i])}</Box>
                                </Box>
                            );
                        })}
                    </Box>
                    );
                })}
            </>
        );
    };

    const changes = useMemo(() => (detail && detail.draft && detail.draft.changes) || [], [detail]);

    if (loading) return <Box style={{ padding: 48, textAlign: 'center' }}><CircularProgress size={28} /></Box>;
    if (!detail || !form) return <Box style={{ padding: 24 }}><Typography color="error">{error || 'Survey not found.'}</Typography></Box>;

    const { live, draft, versions } = detail;

    return (
        <Box style={{ padding: 24, maxWidth: 1100 }}>
            <Button size="small" startIcon={<ArrowBackIcon />} onClick={() => history.push('/surveys/list')} style={{ marginBottom: 8 }}>
                All surveys
            </Button>
            <Typography variant="h5" style={{ fontWeight: 700 }}>{form.title}</Typography>
            <Typography variant="body2" style={{ color: '#666', marginBottom: 16 }}>
                {survey_code} · live v{live ? live.version : '—'}
                {draft ? ` · draft v${draft.version} (from v${draft.based_on_version}), saved ${fmtDateTime(draft.updated_at)}${draft.updated_by ? ` by ${draft.updated_by}` : ''}` : ' · no draft — saving starts one'}
            </Typography>

            {/* Actions */}
            <Card style={{ padding: 12, marginBottom: 16, position: 'sticky', top: 64, zIndex: 2, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <Button variant="contained" color="primary" disabled={!dirty || !!busy} onClick={save}>
                    {busy === 'save' ? 'Saving…' : 'Save draft'}
                </Button>
                <Button variant="outlined" disabled={(!draft && !dirty) || !!busy} onClick={preview}>
                    {busy === 'preview' ? 'Opening…' : 'Preview'}
                </Button>
                <Button variant="outlined" color="primary" disabled={!draft || dirty || !changes.length || !!busy} onClick={() => setConfirm('publish')}>
                    Publish…
                </Button>
                <Button variant="text" disabled={!draft || !!busy} onClick={() => setConfirm('discard')} style={{ color: draft ? '#c62828' : undefined }}>
                    Discard draft
                </Button>
                <Box style={{ flex: 1 }} />
                {dirty && <Chip size="small" label="Unsaved changes" style={{ background: '#fff3e0', color: '#8a5300', fontWeight: 600 }} />}
                {!dirty && draft && <Chip size="small" label={`${changes.length} change${changes.length === 1 ? '' : 's'} from live`} />}
            </Card>

            {error && <Typography color="error" style={{ marginBottom: 12 }}>{error}</Typography>}
            {notice && !error && <Typography style={{ marginBottom: 12, color: '#1b6e3a', fontWeight: 600 }}>{notice}</Typography>}

            {/* Form */}
            <Card style={{ padding: 16, marginBottom: 16 }}>
                {renderField('root', form, 'title', [])}
                <Typography variant="caption" style={{ color: '#888' }}>
                    Only wording can change here. Question order, options and their codes, limits, required answers and
                    show/hide rules stay as they are in code, so results stay comparable across versions.
                </Typography>
            </Card>

            <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography style={{ fontWeight: 600 }}>Introduction</Typography></AccordionSummary>
                <AccordionDetails style={{ display: 'block' }}>{renderNode('intro', form.intro, ['intro'])}</AccordionDetails>
            </Accordion>
            {form.questions.map((q, i) => (
                <Accordion key={q.id}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box>
                            <Typography style={{ fontWeight: 600 }}>
                                <span style={{ fontFamily: 'monospace', color: '#888', marginRight: 8 }}>{q.id}</span>{q.title}
                            </Typography>
                            <Typography variant="caption" style={{ color: '#888' }}>{questionFacts(q).join(' · ')}</Typography>
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails style={{ display: 'block' }}>{renderNode('question', q, ['questions', i])}</AccordionDetails>
                </Accordion>
            ))}
            <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography style={{ fontWeight: 600 }}>Thank-you screen</Typography></AccordionSummary>
                <AccordionDetails style={{ display: 'block' }}>{renderNode('end', form.end, ['end'])}</AccordionDetails>
            </Accordion>

            {/* What the draft changes */}
            {draft && changes.length > 0 && (
                <Card style={{ padding: 16, marginTop: 16 }}>
                    <Typography variant="subtitle1" style={{ fontWeight: 700, marginBottom: 8 }}>
                        Draft v{draft.version}: {changes.length} change{changes.length === 1 ? '' : 's'} from live v{draft.based_on_version}
                    </Typography>
                    <ChangesTable changes={changes} />
                </Card>
            )}

            {/* History */}
            <Card style={{ padding: 16, marginTop: 16 }}>
                <Typography variant="subtitle1" style={{ fontWeight: 700, marginBottom: 8 }}>Versions</Typography>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Version</TableCell><TableCell>Status</TableCell><TableCell>Published</TableCell>
                            <TableCell>By</TableCell><TableCell align="right">Responses</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {versions.map((v) => (
                            <TableRow key={v.version}>
                                <TableCell>v{v.version}{v.based_on_version ? <span style={{ color: '#888' }}> (from v{v.based_on_version})</span> : null}</TableCell>
                                <TableCell>{v.status === 'draft' ? 'Draft' : (live && v.version === live.version ? 'Live' : 'Earlier')}</TableCell>
                                <TableCell>{fmtDateTime(v.published_at)}</TableCell>
                                <TableCell>{v.published_by || '—'}</TableCell>
                                <TableCell align="right">{v.responses}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Card>

            {/* Confirmations */}
            <Dialog open={confirm === 'publish'} onClose={() => !busy && setConfirm(null)} maxWidth="md" fullWidth>
                <DialogTitle>Publish draft v{draft && draft.version}?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" style={{ marginBottom: 12 }}>
                        Everyone who opens the survey from now on sees this version. Responses already
                        received keep the wording they were given.
                    </Typography>
                    <ChangesTable changes={changes} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirm(null)} disabled={!!busy}>Cancel</Button>
                    <Button variant="contained" color="primary" onClick={publish} disabled={!!busy}>
                        {busy === 'publish' ? 'Publishing…' : `Publish v${draft && draft.version}`}
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={confirm === 'discard'} onClose={() => !busy && setConfirm(null)}>
                <DialogTitle>Discard draft v{draft && draft.version}?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">All unpublished wording changes are lost. The live version is not affected.</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirm(null)} disabled={!!busy}>Cancel</Button>
                    <Button onClick={discard} disabled={!!busy} style={{ color: '#c62828' }}>
                        {busy === 'discard' ? 'Discarding…' : 'Discard'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

const ChangesTable = ({ changes }) => (
    <Table size="small">
        <TableHead>
            <TableRow><TableCell>Where</TableCell><TableCell>Before</TableCell><TableCell>After</TableCell></TableRow>
        </TableHead>
        <TableBody>
            {changes.map((c) => (
                <TableRow key={c.path}>
                    <TableCell style={{ fontFamily: 'monospace', fontSize: 12, whiteSpace: 'nowrap', verticalAlign: 'top' }}>{c.path}</TableCell>
                    <TableCell style={{ color: '#a33', verticalAlign: 'top', whiteSpace: 'pre-wrap' }}>{c.before || <em style={{ color: '#aaa' }}>(empty)</em>}</TableCell>
                    <TableCell style={{ color: '#1b6e3a', verticalAlign: 'top', whiteSpace: 'pre-wrap' }}>{c.after || <em style={{ color: '#aaa' }}>(removed)</em>}</TableCell>
                </TableRow>
            ))}
        </TableBody>
    </Table>
);

export default SurveyEditor;
